import { useDateRangeStore } from "./use-date-store";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import moment from "moment";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { Button } from "@/components/ui/button/button";

import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface MonthDates {
  startDate: Date;
  endDate: Date;
}

function getMonthDates(month: number, year: number): MonthDates {
  // Moment.js starts months from 0, so we subtract 1 from the input month
  const startDate = moment([year, month]).startOf("month").toDate();
  const endDate = moment([year, month]).endOf("month").toDate();

  return {
    startDate,
    endDate,
  };
}

export const EmployeesMonthlyReportDatePicker = () => {
  const { dateRange, setDateRange } = useDateRangeStore();

  const [open, setOpen] = useState(false);

  // @ts-ignore
  const [sD, setSD] = useState<Date | undefined>(() => dateRange?.from);
  // @ts-ignore
  const [cD, setCD] = useState<Date | undefined>(() => dateRange?.to);

  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth()
  );

  const handleSave = () => {
    const { startDate, endDate } = getMonthDates(selectedMonth, selectedYear);
    setSD(startDate);
    setCD(endDate);

    setDateRange({
      from: startDate,
      to: endDate,
    });
    setOpen(false);
  };

  const months = [
    { label: "January", value: 0 },
    { label: "February", value: 1 },
    { label: "March", value: 2 },
    { label: "April", value: 3 },
    { label: "May", value: 4 },
    { label: "June", value: 5 },
    { label: "July", value: 6 },
    { label: "August", value: 7 },
    { label: "September", value: 8 },
    { label: "October", value: 9 },
    { label: "November", value: 10 },
    { label: "December", value: 11 },
  ];

  function generateYears(): number[] {
    const currentYear = new Date().getFullYear();
    const endYear = 2024;
    const years: number[] = [];

    for (let year = currentYear; year >= endYear; year--) {
      years.push(year);
    }

    return years;
  }

  const years = generateYears();

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>
        <Button
          id="date"
          variant="outline"
          className={cn(
            "w-[260px] justify-start text-left font-normal",
            !dateRange && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {sD ? (
            cD ? (
              <>
                {format(sD as Date, "LLL dd, y")} -{" "}
                {format(cD as Date, "LLL dd, y")}
              </>
            ) : (
              format(sD, "LLL dd, y")
            )
          ) : (
            <span>Pick a date</span>
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Pick Month and Year</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <Select
              value={String(selectedMonth)}
              onValueChange={(value) => {
                setSelectedMonth(Number(value));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a month" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectGroup>
                  {months.map((month) => {
                    return (
                      <SelectItem key={month.value} value={String(month.value)}>
                        {month.label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Select
              value={String(selectedYear)}
              onValueChange={(value) => {
                setSelectedYear(Number(value));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a year" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectGroup>
                  {years.map((year) => {
                    return (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            onMouseDown={() => {
              setSD(dateRange?.from as Date);
              setCD(dateRange?.to as Date);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <Button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();

              handleSave();
            }}
          >
            Save
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
